import { useState, useEffect } from "react"
import { Plus, Calendar, MapPin, Globe, Users, Clock, Trash2, Eye, ChevronLeft, ChevronRight } from "lucide-react"
import { SchoolLayout } from "../components/ui/school/SchoolLayout"
import api from "../hooks/api/apiClient"
import config from "../../config"

const proto = config.appMode === "prototype"

const EVENT_TYPES = ["academic", "career", "holiday", "exam", "workshop", "guest_speaker", "other"]

const TYPE_COLORS = {
  academic:     { bg: "bg-blue-100",   text: "text-blue-700",   dot: "bg-blue-500" },
  career:       { bg: "bg-teal-100",   text: "text-teal-700",   dot: "bg-teal-500" },
  holiday:      { bg: "bg-green-100",  text: "text-green-700",  dot: "bg-green-500" },
  exam:         { bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500" },
  workshop:     { bg: "bg-violet-100", text: "text-violet-700", dot: "bg-violet-500" },
  guest_speaker:{ bg: "bg-amber-100",  text: "text-amber-700",  dot: "bg-amber-500" },
  other:        { bg: "bg-gray-100",   text: "text-gray-700",   dot: "bg-gray-500" },
}

const MOCK_EVENTS = [
  {
    _id: "1",
    title: "WAEC Prep Workshop",
    description: "Intensive preparation for WAEC Mathematics and English.",
    type: "workshop",
    startDate: new Date(Date.now() + 2 * 86400000).toISOString(),
    endDate: new Date(Date.now() + 2 * 86400000 + 3600000 * 3).toISOString(),
    location: "Main Hall",
    isOnline: false,
    targetClassLevels: ["SS3"],
    rsvpEnabled: true,
    maxAttendees: 80,
    isPublished: true,
    rsvps: [{ userId: "u1", status: "going" }, { userId: "u2", status: "maybe" }],
  },
  {
    _id: "2",
    title: "Career Day — Tech Sector",
    description: "Industry professionals from software, fintech, and AI visit to discuss career paths.",
    type: "career",
    startDate: new Date(Date.now() + 7 * 86400000).toISOString(),
    endDate: new Date(Date.now() + 7 * 86400000 + 3600000 * 5).toISOString(),
    location: "Assembly Ground",
    isOnline: false,
    targetClassLevels: ["SS2", "SS3"],
    rsvpEnabled: true,
    maxAttendees: 200,
    isPublished: true,
    rsvps: [],
  },
  {
    _id: "3",
    title: "Mid-Term Break",
    description: "School closed for mid-term break.",
    type: "holiday",
    startDate: new Date(Date.now() + 14 * 86400000).toISOString(),
    endDate: new Date(Date.now() + 17 * 86400000).toISOString(),
    location: "",
    isOnline: false,
    targetClassLevels: [],
    rsvpEnabled: false,
    isPublished: true,
    rsvps: [],
  },
]

function TypeBadge({ type }) {
  const c = TYPE_COLORS[type] ?? TYPE_COLORS.other
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {type.replace("_", " ")}
    </span>
  )
}

function formatDate(iso) {
  if (!iso) return ""
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
}

function formatTime(iso) {
  if (!iso) return ""
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
}

function EventCard({ event, onDelete, onViewRsvps, onTogglePublish }) {
  const c = TYPE_COLORS[event.type] ?? TYPE_COLORS.other
  return (
    <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden`}>
      <div className={`h-1.5 ${c.dot}`} />
      <div className="p-5 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <TypeBadge type={event.type} />
            <p className="font-bold text-gray-900 mt-1.5">{event.title}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${event.isPublished ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {event.isPublished ? "Published" : "Draft"}
            </span>
          </div>
        </div>

        {event.description && <p className="text-sm text-gray-500 line-clamp-2">{event.description}</p>}

        <div className="space-y-1.5 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span>{formatDate(event.startDate)} {formatTime(event.startDate)}</span>
            {event.startDate !== event.endDate && <span className="text-gray-400">→ {formatDate(event.endDate)}</span>}
          </div>
          {(event.location || event.isOnline) && (
            <div className="flex items-center gap-2">
              {event.isOnline ? <Globe className="w-3.5 h-3.5 text-gray-400" /> : <MapPin className="w-3.5 h-3.5 text-gray-400" />}
              <span>{event.isOnline ? "Online" : event.location}</span>
            </div>
          )}
          {event.targetClassLevels?.length > 0 && (
            <div className="flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-gray-400" />
              <span>{event.targetClassLevels.join(", ")}</span>
            </div>
          )}
        </div>

        {event.rsvpEnabled && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Clock className="w-3.5 h-3.5" />
            <span>RSVP enabled · {event.rsvps?.length ?? 0} responses</span>
            {event.maxAttendees && <span>/ {event.maxAttendees} max</span>}
          </div>
        )}

        <div className="flex gap-2 pt-1 border-t border-gray-100 mt-1">
          <button
            onClick={() => onTogglePublish(event)}
            className="flex-1 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            {event.isPublished ? "Unpublish" : "Publish"}
          </button>
          {event.rsvpEnabled && (
            <button
              onClick={() => onViewRsvps(event)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50"
            >
              <Eye className="w-3 h-3" /> RSVPs
            </button>
          )}
          <button
            onClick={() => onDelete(event._id)}
            className="p-1.5 text-red-400 hover:text-red-600 border border-red-100 rounded-lg hover:bg-red-50"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  )
}

function RsvpModal({ event, onClose }) {
  const [rsvps, setRsvps] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (proto) { setRsvps(event.rsvps ?? []); setLoading(false); return }
    api.get(`/events/${event._id}/rsvps`)
      .then((r) => setRsvps(r.data.data.rsvps))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [event._id])

  const counts = { going: 0, maybe: 0, not_going: 0 }
  rsvps.forEach((r) => { counts[r.status] = (counts[r.status] ?? 0) + 1 })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900">RSVP List</h2>
            <p className="text-sm text-gray-500">{event.title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
        </div>
        <div className="px-6 py-4">
          <div className="flex gap-4 mb-5">
            {[["going", "Going", "bg-green-100 text-green-700"], ["maybe", "Maybe", "bg-amber-100 text-amber-700"], ["not_going", "Not Going", "bg-red-100 text-red-700"]].map(([key, label, cls]) => (
              <div key={key} className={`flex-1 rounded-xl p-3 text-center ${cls}`}>
                <p className="text-2xl font-bold">{counts[key]}</p>
                <p className="text-xs font-semibold mt-0.5">{label}</p>
              </div>
            ))}
          </div>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading…</div>
          ) : rsvps.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No RSVPs yet.</div>
          ) : (
            <div className="space-y-2">
              {rsvps.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <p className="text-sm font-medium text-gray-800">{r.userId?.name ?? r.userId?.firstName ?? `User ${i + 1}`}</p>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.status === "going" ? "bg-green-100 text-green-700" : r.status === "maybe" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                    {r.status.replace("_", " ")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function CreateEventModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "", type: "academic",
    startDate: "", endDate: "", location: "",
    isOnline: false, meetingUrl: "",
    targetClassLevels: "", rsvpEnabled: false,
    maxAttendees: "", isPublished: false,
  })
  const [saving, setSaving] = useState(false)

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }))

  const handleSave = async () => {
    if (!form.title || !form.startDate || !form.endDate) return
    setSaving(true)
    const payload = {
      ...form,
      targetClassLevels: form.targetClassLevels.split(",").map((s) => s.trim()).filter(Boolean),
      maxAttendees: form.maxAttendees ? Number(form.maxAttendees) : undefined,
    }
    if (!proto) {
      await api.post("/events", payload).catch(() => {})
    }
    onCreated({ _id: Date.now().toString(), ...payload, rsvps: [], isPublished: form.isPublished })
    onClose()
    setSaving(false)
  }

  const inp = (label, key, type = "text", placeholder = "", required = false) => (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1">{label}{required && " *"}</label>
      <input
        type={type}
        value={form[key] ?? ""}
        onChange={(e) => set(key, e.target.value)}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
      />
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Create Event</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
        </div>
        <div className="p-6 space-y-4">
          {inp("Event Title", "title", "text", "e.g. Career Day", true)}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" placeholder="Brief description for students" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Event Type</label>
            <select value={form.type} onChange={(e) => set("type", e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {inp("Start Date & Time", "startDate", "datetime-local", "", true)}
            {inp("End Date & Time", "endDate", "datetime-local", "", true)}
          </div>
          <div className="flex items-center gap-3">
            <input id="isOnline" type="checkbox" checked={form.isOnline} onChange={(e) => set("isOnline", e.target.checked)} className="w-4 h-4 rounded text-teal-600" />
            <label htmlFor="isOnline" className="text-sm font-semibold text-gray-700">Online Event</label>
          </div>
          {form.isOnline ? inp("Meeting URL", "meetingUrl", "url", "https://meet.google.com/...") : inp("Location", "location", "text", "e.g. Main Hall")}
          {inp("Target Class Levels (comma-separated)", "targetClassLevels", "text", "e.g. SS2, SS3")}
          <div className="flex items-center gap-3">
            <input id="rsvp" type="checkbox" checked={form.rsvpEnabled} onChange={(e) => set("rsvpEnabled", e.target.checked)} className="w-4 h-4 rounded text-teal-600" />
            <label htmlFor="rsvp" className="text-sm font-semibold text-gray-700">Enable RSVP</label>
          </div>
          {form.rsvpEnabled && inp("Max Attendees (optional)", "maxAttendees", "number", "e.g. 100")}
          <div className="flex items-center gap-3">
            <input id="publish" type="checkbox" checked={form.isPublished} onChange={(e) => set("isPublished", e.target.checked)} className="w-4 h-4 rounded text-teal-600" />
            <label htmlFor="publish" className="text-sm font-semibold text-gray-700">Publish immediately (visible to students)</label>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.title || !form.startDate || !form.endDate} className="px-5 py-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 rounded-lg">
            {saving ? "Saving…" : "Create Event"}
          </button>
        </div>
      </div>
    </div>
  )
}

// Mini calendar month view
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

function MiniCalendar({ events, selectedMonth, onMonthChange, onSelectDate, selectedDate }) {
  const year = selectedMonth.getFullYear()
  const month = selectedMonth.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const eventsByDate = {}
  events.forEach((ev) => {
    const d = new Date(ev.startDate)
    if (d.getFullYear() === year && d.getMonth() === month) {
      const key = d.getDate()
      if (!eventsByDate[key]) eventsByDate[key] = []
      eventsByDate[key].push(ev)
    }
  })

  const cells = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  const today = new Date()
  const isToday = (d) => d === today.getDate() && year === today.getFullYear() && month === today.getMonth()
  const isSelected = (d) => selectedDate && d === selectedDate.getDate() && year === selectedDate.getFullYear() && month === selectedDate.getMonth()

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => onMonthChange(-1)} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronLeft className="w-4 h-4" /></button>
        <h3 className="font-bold text-gray-800">{selectedMonth.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</h3>
        <button onClick={() => onMonthChange(1)} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DAYS.map((d) => <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d[0]}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => d == null ? (
          <div key={i} />
        ) : (
          <button
            key={i}
            onClick={() => onSelectDate(new Date(year, month, d))}
            className={`relative flex flex-col items-center justify-start py-1 rounded-lg text-xs font-medium transition-colors
              ${isSelected(d) ? "bg-teal-600 text-white" : isToday(d) ? "bg-teal-50 text-teal-700 font-bold" : "hover:bg-gray-100 text-gray-700"}`}
          >
            {d}
            {eventsByDate[d] && (
              <div className="flex gap-0.5 mt-0.5">
                {eventsByDate[d].slice(0, 3).map((ev, j) => (
                  <span key={j} className={`w-1 h-1 rounded-full ${TYPE_COLORS[ev.type]?.dot ?? "bg-gray-400"}`} />
                ))}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

export default function EventManagementPage() {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [rsvpEvent, setRsvpEvent] = useState(null)
  const [view, setView] = useState("list") // "list" | "calendar"
  const [filterType, setFilterType] = useState("all")
  const [selectedMonth, setSelectedMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(null)

  const load = () => {
    setLoading(true)
    if (proto) { setEvents(MOCK_EVENTS); setLoading(false); return }
    api.get("/events")
      .then((r) => setEvents(r.data.data.events))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const handleDelete = async (id) => {
    if (!proto) await api.delete(`/events/${id}`).catch(() => {})
    setEvents((prev) => prev.filter((e) => e._id !== id))
  }

  const handleTogglePublish = async (event) => {
    const updated = { ...event, isPublished: !event.isPublished }
    if (!proto) await api.patch(`/events/${event._id}`, { isPublished: updated.isPublished }).catch(() => {})
    setEvents((prev) => prev.map((e) => e._id === event._id ? updated : e))
  }

  const handleMonthChange = (dir) => {
    setSelectedMonth((m) => new Date(m.getFullYear(), m.getMonth() + dir, 1))
  }

  const filtered = events.filter((e) => {
    if (filterType !== "all" && e.type !== filterType) return false
    if (selectedDate) {
      const d = new Date(e.startDate)
      return d.getFullYear() === selectedDate.getFullYear() &&
        d.getMonth() === selectedDate.getMonth() &&
        d.getDate() === selectedDate.getDate()
    }
    return true
  }).sort((a, b) => new Date(a.startDate) - new Date(b.startDate))

  const upcoming = events.filter((e) => new Date(e.startDate) >= new Date()).length
  const published = events.filter((e) => e.isPublished).length
  const totalRsvps = events.reduce((acc, e) => acc + (e.rsvps?.length ?? 0), 0)

  return (
    <SchoolLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">School Events</h1>
            <p className="text-gray-500">Create and manage academic, career, and extracurricular events.</p>
          </div>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-lg transition">
            <Plus className="w-4 h-4" /> Create Event
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            { icon: Calendar, color: "teal", label: "Upcoming Events", value: upcoming },
            { icon: Globe,    color: "blue",  label: "Published",       value: published },
            { icon: Users,    color: "amber", label: "Total RSVPs",      value: totalRsvps },
          ].map(({ icon: Icon, color, label, value }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
              <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center`}>
                <Icon className={`w-5 h-5 text-${color}-600`} />
              </div>
              <div><p className="text-2xl font-bold text-gray-900">{value}</p><p className="text-sm text-gray-500">{label}</p></div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {["list", "calendar"].map((v) => (
              <button key={v} onClick={() => setView(v)} className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all capitalize ${view === v ? "bg-white text-teal-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                {v}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => { setFilterType("all"); setSelectedDate(null) }} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${filterType === "all" && !selectedDate ? "bg-teal-600 text-white border-teal-600" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
              All
            </button>
            {EVENT_TYPES.map((t) => {
              const c = TYPE_COLORS[t]
              return (
                <button key={t} onClick={() => { setFilterType(t); setSelectedDate(null) }} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${filterType === t ? `${c.bg} ${c.text} border-transparent` : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                  {t.replace("_", " ")}
                </button>
              )
            })}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-16 text-gray-400">Loading…</div>
        ) : view === "calendar" ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <MiniCalendar
                events={events}
                selectedMonth={selectedMonth}
                onMonthChange={handleMonthChange}
                onSelectDate={(d) => { setSelectedDate(d); setFilterType("all") }}
                selectedDate={selectedDate}
              />
              {selectedDate && (
                <button onClick={() => setSelectedDate(null)} className="mt-2 w-full py-1.5 text-xs text-gray-500 hover:text-gray-700 underline">
                  Clear date filter
                </button>
              )}
            </div>
            <div className="lg:col-span-2">
              {selectedDate && (
                <p className="text-sm font-semibold text-gray-500 mb-4">
                  Events on {selectedDate.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                </p>
              )}
              {filtered.length === 0 ? (
                <div className="text-center py-16 text-gray-400">No events{selectedDate ? " on this day" : ""}.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filtered.map((e) => (
                    <EventCard key={e._id} event={e} onDelete={handleDelete} onViewRsvps={setRsvpEvent} onTogglePublish={handleTogglePublish} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">No events yet. Create your first event to get started.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((e) => (
                <EventCard key={e._id} event={e} onDelete={handleDelete} onViewRsvps={setRsvpEvent} onTogglePublish={handleTogglePublish} />
              ))}
            </div>
          )
        )}
      </div>

      {showCreate && (
        <CreateEventModal
          onClose={() => setShowCreate(false)}
          onCreated={(ev) => setEvents((prev) => [ev, ...prev])}
        />
      )}
      {rsvpEvent && <RsvpModal event={rsvpEvent} onClose={() => setRsvpEvent(null)} />}
    </SchoolLayout>
  )
}
